import { Cpu, Hourglass, Workflow } from '@orgus/ui/icons';
import type { ReactNode } from 'react';
import type { PipelineCapacity, Ts } from '@orgus/protocol';
import { Drawer, Gauge, cn, formatDuration, plural, useNow } from '@orgus/ui';
import { capacityView, claimsByLabel, claimsByRun } from './model.ts';
import type { CapacityOwners, ClaimGroup, RunGroup } from './model.ts';

/**
 * The worker pool of a pipeline's machine (opened from the Workers figure): who holds the
 * workers, every task, every run and what waits.
 */
export function CapacityDrawer({
  open,
  onOpenChange,
  capacity,
  machineName,
  reserveNote = null,
  owners,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  capacity: PipelineCapacity;
  machineName: string;
  reserveNote?: string | null;
  /** Who holds the workers, in the open (the landing queue's verify, other runs, kept, free). */
  owners?: CapacityOwners;
}) {
  const now = useNow(5000);
  const cap = capacityView(capacity);
  const unit = capacity.label.toLowerCase();
  const reported = capacity.holders !== undefined;
  const busy = claimsByLabel(capacity.holders);
  const waiting = claimsByLabel(capacity.queue);
  const runs = claimsByRun(capacity.holders, capacity.queue);
  const runWord = capacity.runLabel || 'run';

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      width={480}
      eyebrow={<span className="num">{cap.summary}</span>}
      title={`${capacity.label} on ${machineName}`}
      description={`The pool is shared by every ${runWord} on ${machineName}, not only this pipeline.`}
    >
      <div className="flex flex-col gap-6 px-6 py-4">
        <dl className="grid grid-cols-3 gap-3">
          <Stat label="Busy" value={`${Math.min(cap.used, cap.total)} of ${cap.total}`} />
          <Stat label={`${runWord[0]?.toUpperCase()}${runWord.slice(1)}s`} value={capacity.runs != null ? String(capacity.runs) : 'Not reported'} />
          <Stat label="Waiting" value={plural(capacity.waiting, 'task')} />
        </dl>
        {reserveNote && <p className="text-caption text-fg-muted">{reserveNote}</p>}
        {owners && owners.owners.length > 0 && (
          <section className="flex flex-col gap-1">
            <h3 className="flex h-control-sm items-center text-caption font-medium text-fg-muted">Who holds them</h3>
            <ul className="flex flex-col gap-1" aria-label={`Who holds the ${unit}`}>
              {owners.owners.map((o) => (
                <li key={o.key} className="flex min-w-0 items-baseline gap-2 text-ui">
                  <span className={cn('min-w-0 flex-1 truncate', o.key === 'free' || o.key === 'kept' ? 'text-fg-subtle' : 'text-fg-muted')} title={o.detail ? `${o.label}: ${o.detail}` : o.label}>
                    {o.label}
                    {o.detail && <span className="text-fg-subtle">: {o.detail}</span>}
                  </span>
                  <span className="shrink-0 text-fg num">{o.slots}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {!reported ? (
          <p className="rounded-card border border-border-subtle bg-sunken px-3 py-2 text-caption text-fg-muted">
            The agent on {machineName} reports how many {unit} are busy, not who holds them. Update it to the current agent to see the breakdown.
          </p>
        ) : (
          <>
            <Section icon={<Cpu />} title="Busy, by task" empty={`No ${unit} are busy: every ${runWord} on ${machineName} has finished or is waiting.`}>
              {busy.map((g) => (
                <ClaimRow key={g.label} group={g} total={cap.total} now={now} unit={unit} />
              ))}
            </Section>

            <Section icon={<Workflow />} title={`By ${runWord}`} empty={`No ${runWord} holds or waits for ${unit}.`}>
              {runs.map((r) => (
                <RunRow key={r.pid ?? r.run ?? 'unknown'} run={r} now={now} unit={unit} runWord={runWord} />
              ))}
            </Section>

            <Section icon={<Hourglass />} title="Waiting for a worker" empty="Nothing is waiting: a new task gets a worker as soon as one is free.">
              {waiting.map((g) => (
                <li key={g.label} className="flex h-row items-center gap-3">
                  <span className="min-w-0 flex-1 truncate font-mono text-caption text-fg">{g.label}</span>
                  <span className="shrink-0 text-caption text-fg-muted num">
                    {plural(g.claims, 'task')}
                    {g.slots > 0 && `, up to ${plural(g.slots, 'worker')}`}
                  </span>
                </li>
              ))}
            </Section>
          </>
        )}
      </div>
    </Drawer>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1 rounded-card bg-sunken px-3 py-2">
      <dt className="text-caption text-fg-subtle">{label}</dt>
      <dd className="text-lead font-medium text-fg num">{value}</dd>
    </div>
  );
}

function Section({ icon, title, empty, children }: { icon: ReactNode; title: string; empty: string; children: ReactNode[] }) {
  return (
    <section className="flex flex-col gap-1">
      <h3 className="flex h-control-sm items-center gap-2 text-caption font-medium text-fg-muted [&_svg]:size-4 [&_svg]:text-fg-subtle">
        {icon}
        {title}
      </h3>
      {children.length ? <ul className="flex flex-col">{children}</ul> : <p className="py-2 text-caption text-fg-subtle">{empty}</p>}
    </section>
  );
}

const ago = (since: Ts | undefined, now: Ts) => (since != null && now >= since ? formatDuration(Math.floor((now - since) / 60_000) * 60_000 || now - since) : null);

function ClaimRow({ group: g, total, now, unit }: { group: ClaimGroup; total: number; now: Ts; unit: string }) {
  const held = ago(g.since, now);
  const who = [g.reserved ? 'reserved, no run yet' : g.run, g.pids.length ? `pid ${g.pids.join(', ')}` : null, g.claims > 1 ? `${g.claims} claims` : null, held ? `for ${held}` : null]
    .filter(Boolean)
    .join(', ');
  return (
    <li className="flex flex-col gap-1 py-2">
      <span className="flex items-center gap-3 text-ui">
        <span className={cn('min-w-0 flex-1 truncate text-fg', !g.reserved && 'font-mono text-caption')}>{g.label}</span>
        <span className="shrink-0 text-fg num">{plural(g.slots, unit.replace(/s$/, ''), unit)}</span>
      </span>
      <Gauge value={total > 0 ? Math.min(1, g.slots / total) : 0} tone={g.reserved ? 'neutral' : 'working'} label={`${g.label}: ${g.slots} of ${total}`} showValue={false} />
      {who && <span className="text-caption text-fg-subtle num">{who}</span>}
    </li>
  );
}

function RunRow({ run: r, now, unit, runWord }: { run: RunGroup; now: Ts; unit: string; runWord: string }) {
  const held = ago(r.since, now);
  const name = `${runWord[0]?.toUpperCase()}${runWord.slice(1)}${r.pid != null ? `, pid ${r.pid}` : ''}`;
  const parts = [r.slots > 0 ? `${plural(r.slots, unit.replace(/s$/, ''), unit)} on ${plural(r.tasks, 'task')}` : null, r.waiting ? `${plural(r.waiting, 'task')} waiting` : null, held ? `started ${held} ago` : null];
  return (
    <li className="flex flex-col gap-1 py-2 text-ui">
      <span className="flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-fg">{name}</span>
        {r.run && <span className="shrink-0 text-caption text-fg-subtle">{r.run}</span>}
      </span>
      <span className="text-caption text-fg-muted num">{parts.filter(Boolean).join(', ')}</span>
    </li>
  );
}
